import { create } from "@bufbuild/protobuf";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom, useStore } from "jotai";
import { useEffect, useMemo, useRef, type PropsWithChildren, type FC, type Ref } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { ExternalRedirect } from "@/components/flexkit/ExternalRedirect";
import { useToast } from "@/components/flexkit/toast";
import type { EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { useFlagGitHubAppOnboardingEnabled } from "@/hooks/use-flag";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useSegmentTrackError } from "@/hooks/use-segment";
import { useTheme } from "@/hooks/use-theme";
import { useValidateIntegration } from "@/queries/integration-queries";
import { autoPaginate } from "@/queries/pagination";
import { useListRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import { useEnsureOnboardingRunner, useCheckAuthForAllScmIntegrations } from "@/queries/runner-queries";
import { useAuthenticatedUser, useAuthenticatedUserOptions } from "@/queries/user-queries";
import { ConnectToScm } from "@/routes/ona-onboarding/ConnectToScm";
import { ConnectToScmShowcase } from "@/routes/ona-onboarding/ConnectToScmShowcase";
import { GetStartedWithTasks } from "@/routes/ona-onboarding/GetStartedWithTasks";
import { GetStartedWithTasksShowcase } from "@/routes/ona-onboarding/GetStartedWithTasksShowcase";
import { STEP_COLOR_THEMES } from "@/routes/ona-onboarding/onboarding-color-themes";
import {
    currentStepAtom,
    type OnboardingStepSlug,
    ONBOARDING_STEP_SLUG,
    environmentClassAtom,
    scmAuthStatusesAtom,
    useSaveOnboardingStateEffect,
    ONBOARDING_STEP,
    onboardingRunnerAtom,
    type ScmAuthStatusMap,
    lastSelectedScmAuthHostAtom,
} from "@/routes/ona-onboarding/onboarding-state";
import { OnboardingLayout } from "@/routes/ona-onboarding/OnboardingLayout";
import { SelectRepository } from "@/routes/ona-onboarding/SelectRepository";
import { SelectRepositoryShowcase } from "@/routes/ona-onboarding/SelectRepositoryShowcase";
import { isGitHubAppDefinition } from "@/routes/settings/organization/integrations/github-app-utils";
import { formatError } from "@/utils/errors";
import { CreateIntegrationRequestSchema, ValidationCheck_Check } from "gitpod-next-api/gitpod/v1/integration_pb";

const STEP_COMPONENTS = {
    [ONBOARDING_STEP_SLUG.getStartedWithTasks]: {
        content: () => <GetStartedWithTasks />,
        showcase: (ref: Ref<HTMLDivElement>) => <GetStartedWithTasksShowcase ref={ref} />,
    },
    [ONBOARDING_STEP_SLUG.connectToScm]: {
        content: () => <ConnectToScm />,
        showcase: (ref: Ref<HTMLDivElement>) => <ConnectToScmShowcase ref={ref} />,
    },
    [ONBOARDING_STEP_SLUG.selectRepository]: {
        content: () => <SelectRepository />,
        showcase: (ref: Ref<HTMLDivElement>) => <SelectRepositoryShowcase ref={ref} />,
    },
};

export const OnaOnboardingPage: FC<{ step: OnboardingStepSlug }> = ({ step: stepSlug }) => {
    const setCurrentStep = useSetAtom(currentStepAtom);
    const setLastSelectedScmAuthHost = useSetAtom(lastSelectedScmAuthHostAtom);
    const location = useLocation();
    const navigate = useNavigate();
    const store = useStore();
    const { effectiveTheme } = useTheme();

    // When returning from GitHub App installation, the backend redirects to
    // select-your-repository with ?from=github-app-install. Advance the step
    // atom so the bounce-back check below doesn't send the user back to step 1.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("from") !== "github-app-install") return;

        setLastSelectedScmAuthHost("github.com");
        setCurrentStep(ONBOARDING_STEP[ONBOARDING_STEP_SLUG.selectRepository]);

        // Strip the query params so this doesn't re-trigger.
        navigate(`/onboarding/ona/${ONBOARDING_STEP_SLUG.selectRepository}`, { replace: true });
    }, [location.search, setLastSelectedScmAuthHost, setCurrentStep, navigate]);

    useEffect(() => {
        const currentStep = store.get(currentStepAtom);
        const stepFromProp = ONBOARDING_STEP[stepSlug];
        if (stepFromProp.index < currentStep.index) {
            // user is going back to a previous step, we need to update the current step
            setCurrentStep(stepFromProp);
        } else if (stepFromProp.index > currentStep.index) {
            // user is trying to skip a step, we need to update the current step
            navigate(`/onboarding/ona/${currentStep.slug}`, { replace: true });
        }
    }, [stepSlug, navigate, setCurrentStep, store]);

    const { content: StepContent, showcase: renderShowcase } = STEP_COMPONENTS[stepSlug];

    const stepColorTheme = useMemo(() => {
        const variants = STEP_COLOR_THEMES[stepSlug];
        return effectiveTheme === "dark" ? variants.dark : variants.light;
    }, [stepSlug, effectiveTheme]);

    return (
        <BackgroundOnboardingSetup>
            <OnboardingLayout renderShowcase={renderShowcase} stepKey={stepSlug} stepColorTheme={stepColorTheme}>
                <StepContent />
            </OnboardingLayout>
        </BackgroundOnboardingSetup>
    );
};

const BackgroundOnboardingSetup: FC<PropsWithChildren> = ({ children }) => {
    useSaveOnboardingStateEffect();

    const api = useGitpodAPI();
    const location = useLocation();
    const navigate = useNavigate();
    const store = useStore();
    const setOnboardingEnvironmentClass = useSetAtom(environmentClassAtom);
    const setOnboardingRunner = useSetAtom(onboardingRunnerAtom);
    const setScmAuthStatuses = useSetAtom(scmAuthStatusesAtom);
    const setLastSelectedScmAuthHost = useSetAtom(lastSelectedScmAuthHostAtom);
    const lastSelectedScmAuthHost = useAtomValue(lastSelectedScmAuthHostAtom);
    const setCurrentStep = useSetAtom(currentStepAtom);
    const trackError = useSegmentTrackError();
    const { toast } = useToast();
    const hasAutoAdvanced = useRef(false);

    // Issuer identifying which SCM provider the user signed up with.
    // Read from React Router state (set by OnaOnboardingSetupPage) or
    // URL search params (set by the GitHub App login returnTo).
    const loginIssuer = useMemo(() => {
        const fromState = location.state?.loginIssuer as string | null;
        if (fromState) {
            return fromState;
        }
        return new URLSearchParams(location.search).get("issuer");
    }, [location.state, location.search]);

    const {
        data: runnerToUse,
        isLoading: isLoadingRunner,
        error: runnerToUseError,
    } = useEnsureOnboardingRunner({ enabled: true });
    const {
        data: environmentClassForOnboarding,
        error: environmentClassForOnboardingQueryError,
        status: environmentClassForOnboardingStatus,
    } = useQuery({
        queryKey: ["environment-classes", "onboarding", runnerToUse?.runnerId],
        queryFn: async (): Promise<EnvironmentClassEntry> => {
            if (!runnerToUse) {
                throw new Error("Failed to set up a runner");
            }

            const environmentClasses = await autoPaginate(
                (params) => api.environmentService.listEnvironmentClasses(params),
                {
                    filter: {
                        runnerIds: [runnerToUse.runnerId],
                    },
                },
                (response) => response.environmentClasses,
            );

            let entry: EnvironmentClassEntry | null = null;
            // the api currently does not have a reliable mechanism for ordering the environment classes by size
            // we have to rely on the display name to find the smallest environment class
            for (const clazz of environmentClasses) {
                if (clazz.displayName.toLowerCase().includes("small")) {
                    entry = {
                        clazz,
                        runner: runnerToUse,
                    };
                }
            }

            // if we didn't find a small environment class, we'll use the first one
            if (!entry && environmentClasses.length > 0) {
                const firstClass = environmentClasses[0];
                entry = {
                    clazz: firstClass,
                    runner: runnerToUse,
                };
            }

            if (!entry) {
                throw new Error("No environment class available");
            }

            return entry;
        },
        throwOnError: false,
        enabled: !!runnerToUse,
    });

    const { value: gitHubAppOnboardingEnabled } = useFlagGitHubAppOnboardingEnabled();
    const shouldEnsureGitHubApp = loginIssuer === "github.com" && gitHubAppOnboardingEnabled;
    const { data: user } = useAuthenticatedUser();
    const queryClient = useQueryClient();
    const authenticatedUserOptions = useAuthenticatedUserOptions();

    // --- GitHub App integration: ensure it exists, validate, and redirect to install if needed ---

    // Ensure the GitHub App integration exists. Always attempts to create; swallows AlreadyExists.
    // Awaits the authenticated user via queryClient.ensureQueryData so the API calls are authenticated.
    const ensureGitHubAppIntegration = useMutation({
        mutationKey: ["ensureGitHubAppIntegration"],
        mutationFn: async () => {
            await queryClient.ensureQueryData(authenticatedUserOptions);

            const { definitions } = await api.integrationService.listIntegrationDefinitions({});
            const definition = definitions.find(isGitHubAppDefinition);
            if (!definition) {
                throw new Error("GitHub App definition not found");
            }

            // Check if an integration already exists before creating one.
            const { integrations: existing } = await api.integrationService.listIntegrations({});
            const found = existing.find((i) => i.integrationDefinitionId === definition.id);
            if (found) {
                return found.id;
            }

            const { integration } = await api.integrationService.createIntegration(
                create(CreateIntegrationRequestSchema, {
                    integrationDefinitionId: definition.id,
                    enabled: true,
                    runnerId: "",
                    host: "",
                }),
            );
            return integration?.id ?? null;
        },
    });

    const githubAppIntegrationId = ensureGitHubAppIntegration.data ?? null;

    const hasEnsuredGitHubApp = useRef(false);
    useEffect(() => {
        if (shouldEnsureGitHubApp && !hasEnsuredGitHubApp.current) {
            hasEnsuredGitHubApp.current = true;
            ensureGitHubAppIntegration.mutate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldEnsureGitHubApp]);

    // Validate the integration to check if the GitHub App is installed.
    const { data: githubAppValidation } = useValidateIntegration(
        shouldEnsureGitHubApp ? (githubAppIntegrationId ?? undefined) : undefined,
    );

    const githubAppInstallationCheck = githubAppValidation?.checks.find(
        (c) => c.check === ValidationCheck_Check.APP_INSTALLATION,
    );
    const githubAppNeedsInstall = githubAppInstallationCheck !== undefined && !!githubAppInstallationCheck.message;

    const { data: runnerSCMIntegrations } = useListRunnerSCMIntegrations(runnerToUse?.runnerId, {
        enabled: !!runnerToUse,
    });

    // Get relevant SCM integrations (GitHub, Bitbucket)
    const relevantIntegrations = useMemo(() => {
        if (!runnerSCMIntegrations) return [];
        return runnerSCMIntegrations
            .filter(
                (integration) =>
                    integration.host === "github.com" ||
                    integration.host === "bitbucket.org" ||
                    integration.host === "gitlab.com",
            )
            .map((integration) => integration.host);
    }, [runnerSCMIntegrations]);

    // Check auth for all relevant SCM integrations
    const authResults = useCheckAuthForAllScmIntegrations(runnerToUse?.runnerId, relevantIntegrations, {
        refetchConditions: {
            "github.com": lastSelectedScmAuthHost === "github.com",
            "bitbucket.org": lastSelectedScmAuthHost === "bitbucket.org",
            "gitlab.com": lastSelectedScmAuthHost === "gitlab.com",
        },
    });

    useEffect(() => {
        switch (environmentClassForOnboardingStatus) {
            case "pending":
                setOnboardingEnvironmentClass({ type: "pending" });
                break;
            case "success":
                setOnboardingEnvironmentClass({ type: "success", data: environmentClassForOnboarding });
                break;
            case "error":
                setOnboardingEnvironmentClass({ type: "error", error: environmentClassForOnboardingQueryError });
                toast({
                    title: "Failed to find an environment class",
                    description: formatError(environmentClassForOnboardingQueryError),
                });
                trackError("onboarding_find_environment_class_error", environmentClassForOnboardingQueryError);
                console.error(
                    "failed to find an environment class for onboarding",
                    environmentClassForOnboardingQueryError,
                );
                break;
        }
    }, [
        environmentClassForOnboarding,
        environmentClassForOnboardingQueryError,
        environmentClassForOnboardingStatus,
        setOnboardingEnvironmentClass,
        toast,
        trackError,
    ]);

    // Update scmAuthStatuses atom with all auth check results
    useEffect(() => {
        const statusMap: ScmAuthStatusMap = {};
        let firstError: unknown = null;

        for (const [host, result] of Object.entries(authResults)) {
            switch (result.status) {
                case "pending":
                    statusMap[host] = { type: "pending" };
                    break;
                case "success":
                    statusMap[host] = { type: "success", data: result.data };
                    break;
                case "error":
                    statusMap[host] = { type: "error", error: result.error };
                    if (!firstError) {
                        firstError = result.error;
                    }
            }
        }

        setScmAuthStatuses(statusMap);

        // Auto-advance past connect-to-scm when the login issuer's SCM is already authenticated
        // (e.g. non-GitHub token was forwarded during sign-up)
        if (!hasAutoAdvanced.current && loginIssuer && !shouldEnsureGitHubApp) {
            const scmHost = loginIssuer;
            const scmStatus = statusMap[scmHost];
            if (scmStatus?.type === "success" && scmStatus.data?.type === "Authenticated") {
                const currentStep = store.get(currentStepAtom);
                if (currentStep.slug === ONBOARDING_STEP_SLUG.connectToScm) {
                    hasAutoAdvanced.current = true;
                    setLastSelectedScmAuthHost(scmHost);
                    setCurrentStep(ONBOARDING_STEP[ONBOARDING_STEP_SLUG.selectRepository]);
                    navigate(`/onboarding/ona/${ONBOARDING_STEP_SLUG.selectRepository}`, { replace: true });
                }
            }
        }

        // Show error toast only once if any auth check fails
        if (firstError) {
            toast({
                title: "Failed to check SCM authentication",
                description: formatError(firstError),
            });
            trackError("onboarding_check_scm_auth_error", firstError);
            console.error("failed to check scm auth", firstError);
        }
    }, [
        authResults,
        setScmAuthStatuses,
        toast,
        trackError,
        loginIssuer,
        store,
        setLastSelectedScmAuthHost,
        setCurrentStep,
        navigate,
        shouldEnsureGitHubApp,
    ]);

    useEffect(() => {
        if (runnerToUse) {
            setOnboardingRunner({ type: "success", data: runnerToUse });
        } else if (isLoadingRunner) {
            setOnboardingRunner({ type: "pending" });
        } else if (runnerToUseError) {
            setOnboardingRunner({ type: "error", error: runnerToUseError });
            toast({
                title: "Failed to set up a runner",
                description: formatError(runnerToUseError),
            });
            trackError("onboarding_ensure_runner_error", runnerToUseError);
            console.error("failed to set up a runner", runnerToUseError);
        }
    }, [runnerToUse, isLoadingRunner, runnerToUseError, setOnboardingRunner, toast, trackError]);

    // Redirect to the GitHub App install page if the app needs to be installed.
    if (shouldEnsureGitHubApp && githubAppValidation && githubAppIntegrationId && user?.id && githubAppNeedsInstall) {
        const returnTo = `/onboarding/ona/${ONBOARDING_STEP_SLUG.connectToScm}?issuer=github.com`;
        return (
            <ExternalRedirect
                to={`/integrations/app/github/${githubAppIntegrationId}/install?principal=${encodeURIComponent(
                    `user/${user.id}`,
                )}&return_to=${encodeURIComponent(returnTo)}`}
            />
        );
    }

    // If the GitHub App is already installed, advance past connect-to-scm.
    // Uses the same ?from=github-app-install param so OnaOnboardingPage handles the step advancement.
    if (shouldEnsureGitHubApp && githubAppValidation && !githubAppNeedsInstall) {
        return (
            <Navigate to={`/onboarding/ona/${ONBOARDING_STEP_SLUG.selectRepository}?from=github-app-install`} replace />
        );
    }

    return children;
};
